<template>
  <!-- The Better Auth sign-in page (modes "both" and "identity"). Left: the
       tenant's field panel. Right: the form slot, the trust line, the help and
       legal links, and the language switch. Everything comes from the resolved
       tenant; nothing here names a tenant. -->
  <div class="si-page" :class="{ 'si-motion': animate }" data-test="signin-split">
    <FieldPanel
      :brand="brand"
      :branding="branding"
      :deep="deep"
      :eyebrow="t('auth.identity.panel.eyebrow')"
      :headline="headline"
      :lede="lede"
      :chips="chips"
    />

    <main class="si-main">
      <button
        type="button"
        class="si-main__theme"
        :aria-label="darkMode ? 'Switch to light theme' : 'Switch to dark theme'"
        :title="darkMode ? 'Light theme' : 'Dark theme'"
        @click="toggleTheme"
      >
        <v-icon size="18">{{ darkMode ? "light_mode" : "dark_mode" }}</v-icon>
      </button>

      <div class="si-card">
        <div class="si-card__head">
          <h2 class="si-card__title">{{ t("auth.identity.title", { name: brand }) }}</h2>
          <p class="si-card__subtitle">{{ t("auth.identity.subtitle") }}</p>
        </div>

        <slot />

        <p class="si-trust">
          <svg class="si-trust__icon" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 3 4 6v6c0 4.5 3.4 8.4 8 9 4.6-.6 8-4.5 8-9V6z" />
            <path d="m9 12 2 2 4-4" />
          </svg>
          <I18nT keypath="auth.identity.trust" tag="span">
            <template #host><strong data-test="trust-host">{{ host }}</strong></template>
          </I18nT>
        </p>

        <footer v-if="links.length || poweredBy" class="si-links">
          <nav v-if="links.length" class="si-links__row" :aria-label="t('footer.helpLegal')">
            <a
              v-for="link in links"
              :key="link.key"
              :href="link.href"
              :data-test="`link-${link.key}`"
              :target="link.href.startsWith('mailto:') ? undefined : '_blank'"
              rel="noopener noreferrer"
              >{{ t(FOOTER_KEYS[link.key]) }}</a>
          </nav>
          <span v-if="poweredBy" class="si-links__credit" data-test="powered-by">{{ t("footer.poweredBy") }}</span>
        </footer>

        <LanguageSwitch />
      </div>
    </main>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import { I18nT, useI18n } from "vue-i18n";
import { useSessionStore } from "@/stores/session";
import { hasOwnIdentity } from "@/tenant/branding";
import { preAuthLinks, showPoweredBy } from "@/tenant/loginLinks";
import { useThemeToggle } from "@/shell/useThemeToggle";
import FieldPanel from "./FieldPanel.vue";
import LanguageSwitch from "./LanguageSwitch.vue";
import { panelBackground, prefersReducedMotion } from "./panelColours";

const FOOTER_KEYS = { support: "footer.needHelp", terms: "footer.terms", privacy: "footer.privacy" } as const;
/** At most this many chips — a hint of what is inside, not a menu. */
const MAX_CHIPS = 3;

export default defineComponent({
  name: "SignInSplit",
  components: { FieldPanel, LanguageSwitch, I18nT },
  setup() {
    const { t } = useI18n();
    const session = useSessionStore();
    const { darkMode, toggleTheme } = useThemeToggle();

    const brand = computed(() => session.tenant?.name || "Metaship");
    const branding = computed(() => session.tenant?.branding ?? null);
    const homePage = computed(() => session.tenant?.homePage ?? null);
    const loginPage = computed(() => session.tenant?.loginPage ?? null);

    const deep = computed(() => panelBackground(branding.value?.colours?.secondary));
    // The tenant's own welcome copy (App Home settings) wins over the generic text.
    const headline = computed(() => homePage.value?.welcomeTitle?.trim() || brand.value);
    const lede = computed(() => homePage.value?.welcomeText?.trim() || t("auth.identity.panel.lede"));
    const chips = computed(() => {
      if (!homePage.value?.enabled) return [];
      const name = brand.value.trim().toLowerCase();
      return (homePage.value.tiles ?? [])
        .map((tile) => tile.title.trim())
        .filter((title) => title && title.toLowerCase() !== name)
        .slice(0, MAX_CHIPS);
    });

    const host = window.location.host;
    const links = computed(() => preAuthLinks(loginPage.value));
    // An own-brand app never shows the Metaship name (DEV-925).
    const poweredBy = computed(
      () =>
        showPoweredBy(loginPage.value, brand.value) &&
        !hasOwnIdentity(session.tenant?.kind, branding.value),
    );
    const animate = !prefersReducedMotion();

    return {
      t,
      darkMode,
      toggleTheme,
      brand,
      branding,
      deep,
      headline,
      lede,
      chips,
      host,
      links,
      poweredBy,
      animate,
      FOOTER_KEYS,
    };
  },
});
</script>

<style scoped>
.si-page {
  --si-mono: ui-monospace, SFMono-Regular, Menlo, monospace;
  display: flex;
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  overflow-x: hidden;
  background: rgb(var(--v-theme-background));
  color: var(--sh-text);
}
.si-main {
  position: relative;
  /* 56 : 44 with the field panel, side by side above 900px (no wrap gap at 901-999px). */
  flex: 44 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px max(24px, env(safe-area-inset-right, 0px)) max(48px, env(safe-area-inset-bottom, 0px))
    max(24px, env(safe-area-inset-left, 0px));
  box-sizing: border-box;
}
.si-main__theme {
  position: absolute;
  top: max(12px, env(safe-area-inset-top, 0px));
  right: max(12px, env(safe-area-inset-right, 0px));
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--sh-text-60);
  cursor: pointer;
}
.si-main__theme:hover {
  background: var(--sh-hover);
}
.si-main__theme:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}
.si-card {
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  gap: 22px;
}
.si-motion .si-card {
  animation: si-rise 0.6s cubic-bezier(0.2, 0.8, 0.2, 1) both;
}
.si-card__head {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.si-card__title {
  margin: 0;
  font-size: 32px;
  font-weight: 700;
  letter-spacing: -0.8px;
  line-height: 1.15;
  overflow-wrap: anywhere;
}
.si-card__subtitle {
  margin: 0;
  font-size: 15px;
  color: var(--sh-text-60);
}
.si-trust {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(var(--v-theme-primary), 0.1);
  font-size: 12px;
  line-height: 1.5;
  overflow-wrap: anywhere;
}
.si-trust__icon {
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  fill: none;
  stroke: rgb(var(--v-theme-primary));
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.si-links {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 6px 16px;
  font-size: 12px;
  color: var(--sh-text-60);
}
.si-links__row {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
}
.si-links__row a {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  color: var(--sh-text-60);
  text-decoration: none;
}
.si-links__row a:hover {
  color: var(--sh-text);
  text-decoration: underline;
}
.si-links__row a:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}
@keyframes si-rise {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@media (max-width: 900px) {
  .si-page {
    flex-direction: column;
  }
  .si-main {
    flex: 1 0 auto;
    align-items: flex-start;
    padding: 28px max(16px, env(safe-area-inset-right, 0px)) max(40px, env(safe-area-inset-bottom, 0px))
      max(16px, env(safe-area-inset-left, 0px));
  }
  .si-main__theme {
    /* The band above holds the brand; the toggle sits in the form area's corner. */
    top: 8px;
  }
  .si-card__title {
    font-size: 26px;
    padding-right: 44px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .si-card {
    animation: none !important;
  }
}
</style>
